// Boot resilience (2026-10-08, wraptest.nz): a phone that logged in on home
// Wi-Fi and moved to mobile data lost its /api/terms request in the network
// change. terms.ts then showed an empty blocking dialog and the app sat on a
// black screen. Every boot request now retries transient failures with a
// short backoff, and the terms step only blocks when the server really
// requires an acceptance.
//
// Pure (no DOM, no imports) so frontend/tests/bootRetry.test.ts runs it under
// plain Node.

/** A failure worth retrying: no network / network changed / aborted / a
 * timeout or a 5xx-style "try again" status. 4xx answers are final. */
export function isTransientError(e: unknown): boolean {
  if (e === null || e === undefined) return false;
  const status = (e as { status?: unknown }).status;
  if (typeof status === "number") return status === 0 || status === 408 || status === 425 || status === 429 || status >= 500;
  const name = (e as { name?: unknown }).name;
  if (name === "TypeError" || name === "AbortError" || name === "TimeoutError" || name === "NetworkError") return true;
  const msg = String((e as { message?: unknown }).message ?? e).toLowerCase();
  return /failed to fetch|networkerror|network error|load failed|network connection was lost|err_network|err_internet_disconnected|network changed|timed? ?out/.test(msg);
}

export interface RetryOptions {
  /** total tries, including the first */
  attempts?: number;
  baseMs?: number;
  maxMs?: number;
  shouldRetry?: (e: unknown) => boolean;
  sleep?: (ms: number) => Promise<void>;
  onRetry?: (attempt: number, delayMs: number, e: unknown) => void;
}

/** The waits between tries: base, 2x base, 4x base ... capped at maxMs. */
export function backoffDelays(attempts: number, baseMs = 400, maxMs = 4000): number[] {
  const out: number[] = [];
  for (let i = 0; i < attempts - 1; i++) out.push(Math.min(maxMs, baseMs * 2 ** i));
  return out;
}

const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/** Run `fn`, retrying transient failures with backoff. Re-throws the last
 * error (or the first non-transient one at once). */
export async function withRetry<T>(fn: () => Promise<T>, opts: RetryOptions = {}): Promise<T> {
  const delays = backoffDelays(opts.attempts ?? 4, opts.baseMs ?? 400, opts.maxMs ?? 4000);
  const should = opts.shouldRetry ?? isTransientError;
  const sleep = opts.sleep ?? realSleep;
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (attempt >= delays.length || !should(e)) throw e;
      opts.onRetry?.(attempt + 1, delays[attempt], e);
      await sleep(delays[attempt]);
    }
  }
}

export interface TermsFlags {
  terms_accepted: boolean;
  terms_session_ack: boolean;
}

/** What the terms step does at boot, from /api/auth/me (`user`) and the
 * /api/terms answer (`doc`, null when it couldn't be fetched).
 *  - none            nothing due (accepted, and this login's reminder done)
 *  - accept          the blocking accept dialog (the server 403s until then)
 *  - reminder        the once-per-login reminder
 *  - defer-reminder  the reminder is due but the text couldn't be loaded:
 *                    don't block (the server only enforces ACCEPTANCE,
 *                    terms.py TermsMiddleware) - show it later
 *  - unavailable     acceptance is required and the text couldn't be
 *                    loaded: a dialog with Retry, never a dead end */
export type TermsBootAction = "none" | "accept" | "reminder" | "defer-reminder" | "unavailable";

export function termsBootAction(user: TermsFlags, doc: TermsFlags | null): TermsBootAction {
  if (user.terms_accepted && user.terms_session_ack) return "none";
  if (!doc) return user.terms_accepted ? "defer-reminder" : "unavailable";
  if (!doc.terms_accepted) return "accept";
  return doc.terms_session_ack ? "none" : "reminder";
}

/** checkAuth's verdict on a failed /api/auth/me: a 401/403 means "log in";
 * anything transient means "can't reach the server", never the login form
 * (a valid session must not be thrown away because the network blinked). */
export function authFailureKind(e: unknown): "login" | "unreachable" {
  const status = (e as { status?: unknown } | null)?.status;
  if (status === 401 || status === 403) return "login";
  return isTransientError(e) ? "unreachable" : "login";
}
